import React from 'react'
import {BoxProps, HStack, useMultiStyleConfig, VStack, Text, Divider} from '@chakra-ui/react'
import {Button, Icon} from '_uiComponents'
import {ProductVariant} from '_types'
import {useIntl} from 'react-intl'

interface ProductSizeSelectorProps {
    hasSizeChart?: boolean
    variants: Array<ProductVariant>
    notifyMeEnabled?: boolean
    onSizeSelected: (sizeCode: string, variant: ProductVariant) => void
    showOnlySizes?: boolean
    showNotice?: boolean
    onRequestSize: (sizeCode: string, variant: ProductVariant) => void
    openSizeGuide?: () => void
    openHelpWithSizes?: () => void
}

/**
 * @name ProductSizeSelector
 *
 * @component
 *
 * @description
 * Renders the size selector for a product
 *
 * @returns {React.FC} - The rendered product size selector component.
 */
const ProductSizeSelector: React.FC<ProductSizeSelectorProps> = ({
    hasSizeChart,
    variants,
    notifyMeEnabled,
    onSizeSelected,
    showOnlySizes = false,
    showNotice = false,
    onRequestSize,
    openSizeGuide,
    openHelpWithSizes,
}: ProductSizeSelectorProps) => {
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('ProductSizeSelector', {}) as Record<string, BoxProps>

    const handleSizeItemClick = (sizeCode: string, variant: ProductVariant) => {
        onSizeSelected(sizeCode, variant)
    }

    const LABEL_SIZE_GUIDE = formatMessage({id: 'label.pdp.size.guide', defaultMessage: 'Size Guide'})
    const LABEL_SIZES_HELP = formatMessage({id: 'label.pdp.need.help', defaultMessage: 'Need help?'})
    const LABEL_REQUEST_PRODUCT = formatMessage({id: 'button.offcanvas.request.size', defaultMessage: 'Request product'})
    const LABEL_ONLY_ONE_LEFT = formatMessage({id: 'label.pdp.only.one.left', defaultMessage: 'Only one left'})?.toLowerCase()
    const LABEL_SELECT_SIZE_NOTICE = formatMessage({
        id: 'label.pdp.select.size.notice',
        defaultMessage: "Devi selezionare una taglia per poter procedere all'acquisto",
    })

    return (
        <VStack {...styles.container}>
            <VStack {...styles.sizesWrapper}>
                {variants.map((variant, index) => {
                    const {inStock, isOnlyOneLeft} = variant.availability
                    const showRequestSize = !inStock && notifyMeEnabled
                    const showOnlyOneLeft = inStock && isOnlyOneLeft

                    return (
                        <React.Fragment key={variant.ID}>
                            <HStack {...styles.sizeItemWrapper}>
                                <Button
                                    sx={styles.sizeItemButton}
                                    variant="text"
                                    disabled={showOnlySizes ? false : !inStock}
                                    onClick={() => {
                                        handleSizeItemClick(variant.size.code!, variant)
                                    }}
                                    key={`size-${index}`}
                                >
                                    <Text {...styles.sizeItemLabel} {...(!showOnlySizes ? !inStock && styles.sizeItemLabelUnavailable : {})}>
                                        {variant.size.label}
                                    </Text>
                                </Button>

                                {!showOnlySizes && (
                                    <>
                                        {showRequestSize && (
                                            <Button
                                                onClick={() => onRequestSize(variant.size.code!, variant)}
                                                sx={styles.sizeItemRightButton}
                                                variant="text"
                                                key={`size-cta-${index}`}
                                            >
                                                <Text {...styles.buttonRequest}>{LABEL_REQUEST_PRODUCT}</Text>
                                            </Button>
                                        )}
                                        {showOnlyOneLeft && <Text {...styles.labelOnlyOneLeft}>{LABEL_ONLY_ONE_LEFT}</Text>}
                                    </>
                                )}
                            </HStack>
                            <Divider sx={styles.sizeItemDivider} />
                        </React.Fragment>
                    )
                })}
            </VStack>

            {!showOnlySizes && (
                <HStack {...styles.helpCtasContainer}>
                    {hasSizeChart && (
                        <Button onClick={openSizeGuide} sx={styles.helpCta} variant="outlineLight">
                            {LABEL_SIZE_GUIDE}
                        </Button>
                    )}
                    <Button sx={{...styles.helpCta, ...styles.helpSizesCta}} variant="outlineLight" onClick={openHelpWithSizes}>
                        {LABEL_SIZES_HELP}
                    </Button>
                </HStack>
            )}

            {showNotice && (
                <HStack {...styles.noticeContainer}>
                    <Icon name="IconWarning" />
                    <Text {...styles.noticeText}>{LABEL_SELECT_SIZE_NOTICE}</Text>
                </HStack>
            )}
        </VStack>
    )
}

export default ProductSizeSelector
